import React from "react";
import { ArrowRightIcon, TagIcon, ListBulletIcon } from "@heroicons/react/24/outline";
import { INCIDENT_CATEGORIES_WITH_OPTIONS } from "../../constants/AppConstants";

const TypeCard = ({ category, subtype, variant = "default" }) => {
  const isNew = variant === "new";
  return (
    <div
      className={`rounded-xl border px-4 py-3 space-y-2 ${
        isNew
          ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700"
          : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
      }`}
    >
      <div className="flex items-center gap-2">
        <TagIcon className={`w-3.5 h-3.5 shrink-0 ${isNew ? "text-blue-500" : "text-gray-400"}`} />
        <span className={`text-xs font-bold truncate ${isNew ? "text-blue-600 dark:text-blue-400" : "text-gray-800 dark:text-gray-200"}`}>
          {category}
        </span>
      </div>
      {subtype && (
        <div className="flex items-center gap-2 pl-0.5">
          <ListBulletIcon className="w-3.5 h-3.5 shrink-0 text-gray-400" />
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtype}</span>
        </div>
      )}
    </div>
  );
};

const ChangeTypeConfirmDialog = ({ show, currentTypeId, currentSubtype, newTypeId, newSubtype, onConfirm, onCancel }) => {
  if (!show) return null;

  const currentCategory = INCIDENT_CATEGORIES_WITH_OPTIONS.find((c) => c.id === currentTypeId);
  const newCategory = INCIDENT_CATEGORIES_WITH_OPTIONS.find((c) => c.id === newTypeId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">

        {/* Primary top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600" />

        {/* Header */}
        <div className="px-5 pt-5 pb-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <div className="pt-0.5">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Change Incident Type?</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
              Switching types will clear your current category and option selection. This action cannot be undone.
            </p>
          </div>
        </div>

        {/* Divider */}
        <div className="mx-5 border-t border-gray-100 dark:border-gray-800" />

        {/* Comparison */}
        <div className="px-5 py-4">
          <div className="flex items-center gap-2">
            {/* Current */}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1.5">Current</p>
              <TypeCard
                category={currentCategory?.name || currentTypeId}
                subtype={currentSubtype}
                variant="default"
              />
            </div>

            {/* Arrow */}
            <div className="shrink-0 mt-5">
              <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                <ArrowRightIcon className="w-3.5 h-3.5 text-gray-400" />
              </div>
            </div>

            {/* New */}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-1.5">Switching to</p>
              <TypeCard
                category={newCategory?.name || newTypeId}
                subtype={newSubtype}
                variant="new"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 pb-5">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
          >
            Keep Current
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm shadow-blue-200 dark:shadow-none"
          >
            Yes, Switch
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeTypeConfirmDialog;
